import { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import { Text, AlertTitle } from '@chakra-ui/react';
import AGGridMain from '@/components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../../hooks/useColumnState';
import { getJobsQuery } from '@tcap/data-router/loaders/jobLoader';
import config from '../../../../config.json';
import { Alert, AlertIcon } from '@/components/v4';
import JobStatusCellRenderer from '../cellRenderer/JobStatusCellRenderer';
import JobStatuses from '../utils/jobStatuses';
import TimeCellRenderer from 'components/tables/AGGrid/CustomCells/TimeCellRenderer';

const Jobs = () => {
    const tableRef = useRef(null);
    const dispatch = useDispatch();

    // scope from state
    const { currentScope } = useSelector((state) => ({ ...state.login }));
    const { currentCompany, currentWholesaler, currentPartner } = useSelector(
        (state) => ({ ...state.navigationLists }),
    );

    // AGGrid columns
    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Name',
                field: 'description',
            },
            {
                headerName: 'Status',
                field: 'jobStatus',
                width: 160,
                cellRenderer: (params) => (
                    <JobStatusCellRenderer
                        value={params.value}
                        comments={params.data?.comments}
                    />
                ),
                cellStyle: { display: 'flex', alignItems: 'center' },
                sortable: true,
                comparator: (a, b) => a - b,
                filter: true,
                valueFormatter: (params) => {
                    const statusLookup = JobStatuses[params?.value];
                    const displayName = statusLookup?.displayedName;
                    return displayName;
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const statusLookup = JobStatuses[params?.value];
                        const displayName = statusLookup?.displayedName;
                        if (!isSelectedAll) {
                            return <Text isTruncated>{`${displayName}`}</Text>;
                        } else {
                            return '(Select All)';
                        }
                    },
                },
            },
            {
                headerName: 'Worker Type',
                field: 'workerType',
                valueFormatter: (params) =>
                    config.jobs.workersJSON[params.value] || params.value,
            },
            {
                headerName: 'Worker ID',
                field: 'workerID',
                hide: currentScope !== 80,
            },
            {
                headerName: 'Last Updated At',
                field: 'lastUpdatedTS',
                cellRenderer: TimeCellRenderer,
            },
            {
                headerName: 'Created At',
                field: 'createTS',
                sort: 'desc',
                cellRenderer: TimeCellRenderer,
            },
            {
                headerName: 'Detail Type',
                field: 'detailType',
                valueFormatter: (params) =>
                    config.jobs.detailsJSON[params.value] || params.value,
                hide: true,
            },
            {
                headerName: 'Details',
                field: 'details',
                hide: true,
            },
            {
                headerName: 'Owner ID',
                field: 'ownerID',
                hide: true,
            },
            {
                headerName: 'Wholesaler ID',
                field: 'wholesalerID',
                hide: true,
            },
            {
                headerName: 'Partner ID',
                field: 'partnerID',
                hide: true,
            },
            {
                headerName: 'Company ID',
                field: 'companyID',
                hide: true,
            },
        ],
        [currentScope],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const {
        data: jobs = [],
        isRefetching,
        isFetching,
        refetch,
        status,
    } = useQuery({
        ...getJobsQuery({
            wholesalerID: currentScope === 60 ? currentWholesaler : null,
            partnerID: currentScope === 40 ? currentPartner : null,
            companyID: currentScope === 20 ? currentCompany : null,
        }),
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 2,
    });

    const refreshFn = () => {
        dispatch({ type: 'CHANGE_TABLE_FILTERING' });
        // Returned so AGGridMain's refresh button can await the request and
        // keep its loading overlay up for the real duration.
        return refetch();
    };

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: jobs,
        }),
        [columns, jobs],
    );

    // Check if entity is selected based on scope
    const hasRequiredEntity =
        (currentScope === 20 && currentCompany) ||
        (currentScope === 40 && currentPartner) ||
        (currentScope === 60 && currentWholesaler) ||
        currentScope === 80; // Global scope doesn't require selection

    if (!hasRequiredEntity) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    return (
        <>
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Jobs
            </Text>
            <AGGridMain
                gridRef={tableRef}
                tableData={tableDataWithColumns}
                masterDetail={false}
                isLoading={status === 'loading'}
                isFetching={isFetching}
                isRefetching={isRefetching}
                titleName="Jobs"
                refetch={refreshFn}
                onColumnVisible={onColumnVisible}
            />
        </>
    );
};

export default Jobs;
